import { breakpoints, Button, Inline, Stack } from '@etg/wings';
import { DangerousHtmlContent } from '@eti/components';
import { useTranslation } from '@eti/providers';
import { price } from '@eti/utils';
import { useEffect } from 'react';
import type { TripPriceChange } from '@eti/schema-types';
import { paymentMethodCodes } from '../../constants/paymentMethods';
import { usePaymentMethod } from '../../payment-methods/context/PaymentMethodsContext';
import { klarnaCodes } from '../../payment-methods/klarna/constants';

const { PAYPAL, PAYPAL_PAY_LATER } = paymentMethodCodes;

const getPriceDifference = (priceChanges: TripPriceChange[]) =>
  priceChanges.reduce(
    (acc, priceChange) =>
      acc + (priceChange?.priceNew?.price?.value || 0) - (priceChange?.priceOld?.price?.value || 0),
    0,
  );

export const isPriceIncrease = (priceChanges: TripPriceChange[]) =>
  Boolean(getPriceDifference(priceChanges) > 0);

const getTotalPriceFor = (
  key: keyof Pick<TripPriceChange, 'priceOld' | 'priceNew'>,
  priceChanges: TripPriceChange[],
) => priceChanges.reduce((acc, priceChange) => acc + (priceChange[key]?.price?.value || 0), 0);

type SelectedPaymentMethod = {
  code: string;
  type: string;
  id: string;
};

const shouldShowOnlyDeclineButton = (selectedMethod: SelectedPaymentMethod | null) => {
  return (
    selectedMethod &&
    (klarnaCodes.includes(selectedMethod.code) ||
      selectedMethod.code === PAYPAL ||
      selectedMethod.code === PAYPAL_PAY_LATER)
  );
};

type PriceChangeContentProps = {
  isPayInternal?: boolean;
  onAcceptPriceChange: (cb?: () => void) => void;
  onDeclinePriceChange: () => void;
  priceChanges: TripPriceChange[];
  selectedPaymentMethod: SelectedPaymentMethod | null;
  populatePriceChangeDisplayed: (priceChangeText: string) => void;
};

const PriceChangeContent = ({
  isPayInternal,
  onAcceptPriceChange,
  onDeclinePriceChange,
  priceChanges,
  selectedPaymentMethod,
  populatePriceChangeDisplayed,
}: PriceChangeContentProps) => {
  const { t } = useTranslation();
  const { onSubmit } = usePaymentMethod();
  const priceDifference = getPriceDifference(priceChanges);
  const oldTotalPrice = getTotalPriceFor('priceOld', priceChanges);
  const newTotalPrice = getTotalPriceFor('priceNew', priceChanges);
  const messageTextKey = isPriceIncrease(priceChanges)
    ? 'Air.Result.ImportantTripInformation.MetaPriceChange.Increase.Text'
    : 'Air.Result.ImportantTripInformation.MetaPriceChange.Decrease.Text';

  const content = t(
    messageTextKey,
    price.formatPrice(Math.abs(priceDifference)),
    price.formatPrice(oldTotalPrice),
    price.formatPrice(newTotalPrice),
  );

  useEffect(() => {
    populatePriceChangeDisplayed(content);
  }, [content, populatePriceChangeDisplayed]);

  const shouldShowAcceptButton = !(
    isPayInternal || shouldShowOnlyDeclineButton(selectedPaymentMethod)
  );

  const handleOnAccept = () => {
    onAcceptPriceChange(onSubmit);
  };

  const handleOnDecline = () => {
    onDeclinePriceChange();
  };

  return (
    <Stack spacing={24}>
      <Stack>
        <DangerousHtmlContent component="p" content={content} />
        {isPayInternal && (
          <DangerousHtmlContent
            component="p"
            content={t('Payment.ZeroAmountCart.PriceChange.Information')}
            dataTestId="payment-tripChangeModal-InternalContent"
          />
        )}
      </Stack>
      <Inline align="end" collapseBelow={breakpoints._560}>
        <Button
          data-testid="trip-change-decline-button"
          onClick={handleOnDecline}
          variant="secondary"
        >
          {t('PaymentPage.TripChangeModal.Price.Decline')}
        </Button>
        {shouldShowAcceptButton && (
          <Button
            data-testid="trip-change-accept-button"
            onClick={handleOnAccept}
            variant="primary"
          >
            {t('PaymentPage.TripChangeModal.Price.Accept')}
          </Button>
        )}
      </Inline>
    </Stack>
  );
};

export default PriceChangeContent;
